import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import React, {
  memo,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import {
  getFilterSubItems,
  getQueryActivity,
  getQueryProjects,
  getQueryTrackers,
  getQueryUser,
  getFilterIssuePriorities,
  getFilterIssueRoles,
  getFilterIssueStatuses,
  getFilterIssueUserGroups,
  getFilterIssueWatcher,
  getFilterProjectStatuses,
  getQueryIssues,
  getTimesheets,
  getFilterCalculationTypes,
} from 'common/api';
import ClearIcon from 'common/shared-ui/src/icons/ClearIcon';

import { store } from '../../../../../../src/components/app/redux';
import { useDebouncedFunction } from '../../../../utils/debounce';
import { useTranslation } from '../../../../utils/getLocalString';
import { LanguageMap } from '../../../../utils/languageMap';
import CheckboxIcon from '../../icons/CheckboxIcon';
import CheckboxIconChecked from '../../icons/CheckboxIconChecked';
import ChevronIcon from '../../icons/ChevronIcon';

import FilterDateBetween from './components/FilterDateBetween';

// Original class: autoCompletePaper
const StyledAutoCompleteBox = styled(Box)(({ theme }) => ({
  '&.MuiAutocomplete-root': {
    backgroundColor: '#fff',
    borderRadius: theme.spacing(4),
    marginTop: theme.spacing(1),
    '& .MuiAutocomplete-listbox': {
      padding: 0,
      '& .MuiAutocomplete-option': {
        maxHeight: 28,
      },
      '& .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
        {
          backgroundColor: '#DCDFF4',
        },
      '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
        backgroundColor: 'rgba(220, 223, 244, .6)',
      },
    },
  },
}));

// Original class: filterInput
const StyledFilterInputButton = styled(Button, {
  shouldForwardProp: prop => !['inputBoxStyle', 'activeStyled'].includes(prop),
})(({ theme, inputBoxStyle, activeStyled }) => ({
  position: 'relative',
  width: '100%',
  padding: '10px 16px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  fontWeight: '400',
  height: '40px',
  justifyContent: 'flex-start',
  '&:hover': {
    backgroundColor: '#fff !important',
  },
  ...(activeStyled && {
    borderRadius: theme.spacing(4),
    backgroundColor: '#fff !important',
    outline: '1px solid rgba(52, 72, 255, 1)',
  }),
}));
// Original class: filterInput
const StyledFilterInputTextField = styled(TextField, {
  shouldForwardProp: prop =>
    ![
      'secondInputStyle',
      'defaultFirstStepStyle',
      'defaultActiveStyle',
    ].includes(prop),
})(
  ({ theme, secondInputStyle, defaultFirstStepStyle, defaultActiveStyle }) => ({
    position: 'relative',
    width: '100%',

    '& .MuiButtonBase-root': {
      maxWidth: 'unset',
      zIndex: 1991,
      ...(defaultFirstStepStyle && {
        height: 42,
        borderRadius: theme.spacing(4),
      }),
    },

    '& .MuiOutlinedInput-notchedOutline': {
      border: '0 !important',
    },

    '& .MuiInputBase-root': {
      padding: theme.spacing(1, 0, 0.5, 0),
      position: 'relative',
      height: 'unset',
      minHeight: 40,
      overflowY: 'auto',
      backgroundColor: '#fff',
      '&::-webkit-scrollbar': {
        display: 'none',
      },

      '&:hover': {
        backgroundColor: '#fff!important',
        ...(defaultActiveStyle && {
          backgroundColor: '#fff',
          borderRadius: theme.spacing(4),
          outline: '1px solid rgba(52, 72, 255, 1)',
        }),
      },

      '&:focus': {
        backgroundColor: '#fff',
        border: 0,
      },

      '& .MuiAutocomplete-input': {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        padding: theme.spacing(0, 0, 0, 4),
        zIndex: 1990,
        minWidth: 50,
        height: '100%',

        '&::placeholder': {
          ...theme.typography.h5,
          color: '#7174AC',
          opacity: 1,
        },

        ...(defaultFirstStepStyle && {
          width: '100%',
          '&::placeholder': {
            ...theme.typography.h4,
            color: 'rgba(33, 35, 70, 1)',
            opacity: 1,
          },
        }),
      },

      ...(secondInputStyle && {
        padding: theme.spacing(1, 0, 1.25, 0),
      }),

      ...(defaultFirstStepStyle && {
        '& input': {
          width: '100%',
          '&::placeholder': {
            ...theme.typography.h4,
            color: 'rgba(33, 35, 70, 1)',
            opacity: 1,
          },
        },
      }),

      ...(defaultActiveStyle && {
        backgroundColor: '#fff',
        borderRadius: theme.spacing(4),
        outline: '1px solid rgba(52, 72, 255, 1)',
      }),
    },
  }),
);
// Original class: autoCompletePopper
const StyledAutoCompletePopper = styled(Popper, {
  shouldForwardProp: prop =>
    !['defaultFirstStepPaperStyle', 'dateFirstStepPaperStyle'].includes(prop),
})(({ theme, defaultFirstStepPaperStyle, dateFirstStepPaperStyle }) => ({
  zIndex: 2000,
  minWidth: 280,
  maxWidth: 592,
  backgroundColor: '#fff',
  borderRadius: theme.spacing(2.5),
  width: 'fit-content',
  border: '1px solid #E4E4EF',

  ...(defaultFirstStepPaperStyle && {
    position: 'absolute',
    top: 40,
    left: 0,
    width: 'auto',
    minWidth: 300,
    maxWidth: 300,
  }),

  ...(dateFirstStepPaperStyle && {
    maxWidth: 'none',
  }),

  '& .MuiAutocomplete-listbox': {
    padding: 0,
    '& .MuiAutocomplete-option': {
      maxHeight: 28,
    },
    '& .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
      {
        backgroundColor: '#DCDFF4',
      },
    '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
      backgroundColor: 'rgba(220, 223, 244, .6)',
    },
  },
  '& .MuiDateCalendar-root': {
    height: 'auto',
  },
}));

// Original class: autoCompletePopper
const StyledAutoCompletePaper = styled(Paper, {
  shouldForwardProp: prop =>
    !['defaultFirstStepPaperStyle', 'dateFirstStepPaperStyle'].includes(prop),
})(({ theme, defaultFirstStepPaperStyle, dateFirstStepPaperStyle }) => ({
  zIndex: 2000,
  minWidth: 280,
  maxWidth: 592,
  backgroundColor: '#fff',
  borderRadius: theme.spacing(2.5),
  width: 'fit-content',
  border: '1px solid #E4E4EF',

  ...(defaultFirstStepPaperStyle && {
    position: 'absolute',
    top: 40,
    left: 0,
    width: 'auto',
    minWidth: 300,
    maxWidth: 300,
  }),

  ...(dateFirstStepPaperStyle && {
    maxWidth: 'none',
  }),

  '& .MuiAutocomplete-listbox': {
    padding: 0,
    '& .MuiAutocomplete-option': {
      maxHeight: 28,
    },
    '& .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
      {
        backgroundColor: '#DCDFF4',
      },
    '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
      backgroundColor: 'rgba(220, 223, 244, .6)',
    },
  },
}));

// Original class: secondStepInput
const StyledSecondStepInput = styled(Select)(theme => ({
  '&.MuiInputBase-root .MuiNativeSelect-select': {
    zIndex: 1990,
  },

  maxHeight: 24,
  width: '100%',
}));
// Original class: calendarResplanRoot
const StyledCalendarResplanRoot = styled(LocalizationProvider)(theme => ({
  '&.MuiCalendarPicker-root': {
    width: 'auto !important',
    '& .MuiSvgIcon-root': {
      width: 24,
      height: 24,
    },
  },

  '&.MuiMonthPicker-root': {
    width: 'auto !important',
  },

  '& .Mui-selected': {
    backgroundColor: '#dcdff4 !important',
    border: '1px solid #E4E4EF',
    borderRadius: '16px !important',
    color: '#7174AC !important',
  },

  '& .MuiPickersCalendarHeader-root': {
    display: 'none !important',
  },
}));
// Original class: boolClearButton
const StyledBoolClearButton = styled(Button)(theme => ({
  maxHeight: 22,
}));
// Original class: booleanLabel
const StyledBooleanLabel = styled(FormControlLabel)(theme => ({
  '&.MuiFormControlLabel-root': {
    margin: 0,
  },
}));
// Original class: groupByFilter
const StyledGroupByFilter = styled(ToggleButtonGroup)(theme => ({
  backgroundColor: '#dcdff4 !important',
  border: '0px !important',
  height: '40px',
  borderRadius: '16px !important',
  maxWidth: '100%',

  '& .Mui-selected': {
    backgroundColor: '#ffffff !important',
    border: '1px solid #E4E4EF',
    borderRadius: '16px !important',
  },

  '& .MuiToggleButtonGroup-grouped': {
    background: '#dcdff4',
    color: '#7174AC',
    borderRadius: '16px',
    border: '0px !important',
    padding: '10px 6px',
    fontSize: '14px',
    fontWeight: '600',
  },
}));
// Original class: resPlanToggle
const StyledResPlanToggle = styled(ToggleButton)(theme => ({
  '&.MuiButtonBase-root': {
    '&.MuiToggleButton-root': {
      textTransform: 'none',
    },
  },
}));
// Original class: StatusFilterItem
const StyledStatusFilterItem = styled(ToggleButtonGroup)(theme => ({
  backgroundColor: '#ffffff !important',
  border: '0px !important',
  height: '40px',
  borderRadius: '16px !important',
  maxWidth: '100%',

  '& .Mui-selected': {
    backgroundColor: '#dcdff4 !important',
    border: '1px solid #E4E4EF',
    borderRadius: '16px !important',
  },

  '& .MuiToggleButtonGroup-grouped': {
    background: '#ffffff',
    color: '#7174AC',
    borderRadius: '16px',
    border: '0px !important',
    padding: '10px 6px',
    fontSize: '12px',
  },
}));
// Original class: subItemChip
const StyledSubItemChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    display: 'none',
  },
}));
// Original class: typeArrow
const StyledTypeArrow = styled(Box)(theme => ({
  background: '#fff',
}));
// Original class: filterAutoComplete
const StyledFilterAutoComplete = styled(Autocomplete)(theme => ({
  '&.MuiAutocomplete-root': {
    '& .MuiAutocomplete-listbox': {
      padding: 0,
    },
  },
}));
// Original class: filterChip
const StyledFilterChip = styled(Chip)(({ theme }) => ({
  '&.MuiChip-root': {
    border: '1px solid #E4E4EF',
    height: '30px',
    backgroundColor: '#ffffff',
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    zIndex: 1991,
  },
}));
const CustomFilter = ({
  // setValues MUST INCLUDE "chosenFirstStepItems" OF TYPE OBJECT!
  setValues,
  resplan,
  firstStepItems = [],
  secondStepItems = [],
  chosenFirstStepItems = {},
  chosenSecondStepItems,
  defaultStep = 0,
  fixedAnchorEl,
  firstStepValues,
  isClear,
  filtersOnlySelect,
}) => {
  const getLocalString = useTranslation();
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const state = store.getState();
  const [filterOptions, setFilterOptions] = useState({
    availableFirstStepItems: [
      ...firstStepItems.map((filter, index) => {
        const filterLabel = filter.fieldName;
        return {
          label: filterLabel,
          value: filter.fieldId,
          fieldType: filter.fieldType,
          type: filter.fieldType !== 'text' ? 'eq' : 'in',
          notParsedType: filter.fieldType !== 'text' ? 'eq' : 'in',
          locked: filter.locked ? filter.locked : false,
          subLabels: [],
          id: index,
        };
      }),
    ],
    // IF OBJECT HAS "value" ATTRIBUTE = OBJECT NOT FORMATTED
    chosenFirstStepItems: chosenFirstStepItems,
    secondStepInputText: '',
    availableSecondStepItems: secondStepItems.map(item => ({
      valueName: item.valueName,
      valueId: item.valueId,
      isChecked: item.isChecked,
    })),
    chosenSecondStepItems: chosenSecondStepItems,
    firstStepValues: firstStepValues || {
      value: '',
      valueName: '',
      valueId: null,
      fieldType: '',
      type: '',
    },
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [isFirstStepPopperOpen, setIsFirstStepPopperOpen] = useState(false);
  const [isSecondStepPopperOpen, setIsSecondStepPopperOpen] = useState(false);
  const [isSecondStepLoading, setIsSecondStepLoading] = useState(false);
  const [isDefaultFirstStepOpen, setIsDefaultFirstStepOpen] = useState(false);
  const [isClearDate, setIsClearDate] = useState(isClear);
  const [language, setLanguage] = useState('ru');

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);
  useEffect(() => {
    if (
      defaultStep === 0 &&
      filterOptions.chosenFirstStepItems !== chosenFirstStepItems
    ) {
      setFilterOptions(prevState => ({
        ...prevState,
        chosenFirstStepItems: chosenFirstStepItems,
      }));
    }
  }, [
    chosenFirstStepItems,
    defaultStep,
    filterOptions.chosenFirstStepItems,
    isClear,
  ]);

  const handleFormatFirstStepItems = useCallback(() => {
    const filteredFilters = Object.entries(
      filterOptions.chosenFirstStepItems,
    ).reduce((acc, rec) => {
      if (rec[0].split('').at(-1) === '_') {
        if (rec[1].subLabels?.length < 1) {
          return acc;
        }
        return {
          ...acc,
          [rec[0].split('').slice(0, -1).join('')]: rec[1],
        };
      }
      return {
        ...acc,
        [rec[0]]: rec[1],
      };
    }, {});
    setFilterOptions(prevState => {
      return {
        ...prevState,
        chosenFirstStepItems: filteredFilters,
      };
    });
    return setValues(filteredFilters);
  }, [filterOptions.chosenFirstStepItems, setValues]);

  const handleCloseAll = useCallback(() => {
    setIsDefaultFirstStepOpen(false);
    setIsSecondStepPopperOpen(false);
    setIsFirstStepPopperOpen(false);
    const notParsedFilter = Object.keys(
      filterOptions.chosenFirstStepItems,
    ).find(label => label.split('').at(-1) === '_');
    if (notParsedFilter) {
      handleFormatFirstStepItems();
    }
  }, [filterOptions.chosenFirstStepItems, handleFormatFirstStepItems]);

  const handleOpenFilterFirstStep = useCallback(
    e => {
      if (!anchorEl) {
        setAnchorEl(e.currentTarget);
      }
      if (!isSecondStepPopperOpen) {
        setIsFirstStepPopperOpen(true);
      } else {
        setIsFirstStepPopperOpen(false);
        setIsSecondStepPopperOpen(false);
      }
      forceUpdate();
    },
    [anchorEl, isSecondStepPopperOpen],
  );

  const handleGetNewQueryIssues = useCallback(
    async ({ id, value }) => {
      try {
        const { data: newSecondStepItems } = await getQueryIssues({
          searchText: value
            ? value
            : filterOptions.chosenFirstStepItems[id]?.subLabels
                .filter(item => item)
                .map(item => item.valueId)
                .join('||'),
        });
        if (!newSecondStepItems) {
          return;
        }
        const updatedItems = newSecondStepItems.map(item => ({
          ...item,
          valueName: item.valueName.trim(),
        }));
        setIsSecondStepLoading(true);
        setFilterOptions(prevState => ({
          ...prevState,
          availableSecondStepItems: updatedItems,
        }));
        setIsSecondStepLoading(false);
        return updatedItems;
      } catch (error) {
        console.error('ERROR WITH GET ISSUES', error);
      }
    },
    [filterOptions.chosenFirstStepItems],
  );

  const handleChangeFilter = useCallback(
    async (e, newValue, _, { option }) => {
      const isOptionIncludesValues = Object.values(
        filterOptions.chosenFirstStepItems,
      )
        .map(item => item.value)
        .includes(option.value);
      setAnchorEl(fixedAnchorEl.current);
      setIsFirstStepPopperOpen(false);
      setIsSecondStepLoading(true);
      setIsSecondStepPopperOpen(true);
      if (
        isOptionIncludesValues &&
        !option.hasOwnProperty('isUpdateFirstStep')
      ) {
        const filteredItems = Object.values(
          filterOptions.chosenFirstStepItems,
        ).reduce((acc, rec) => {
          if (rec.value === option.value) {
            return acc;
          }
          return {
            ...acc,
            [rec.value]: rec,
          };
        }, {});
        setValues(filteredItems);
        setIsSecondStepLoading(false);
        setIsSecondStepPopperOpen(false);
        return setFilterOptions(prevState => ({
          ...prevState,
          chosenFirstStepItems: filteredItems,
        }));
      }
      let rawSubItems = [];
      let projectId =
        state.user.project?.redmineId === undefined
          ? null
          : state.user.project.redmineId;
      try {
        switch (option.fieldType) {
          case 'project': {
            const { data: queryProjects } = await getQueryProjects();
            rawSubItems = queryProjects;
            break;
          }
          case 'issue': {
            const items = await handleGetNewQueryIssues({ id: option.value });
            if (items) {
              rawSubItems = items.map(item => ({ ...item, isChecked: true }));
            }
            break;
          }
          case 'tracker': {
            const { data: queryTrackers } = await getQueryTrackers(projectId);
            rawSubItems = queryTrackers;
            break;
          }
          case 'user': {
            const { data: queryUser } = await getQueryUser(
              option.value,
              projectId,
            );
            queryUser[0].valueName = getLocalString('labels', 'me');
            rawSubItems = queryUser;
            break;
          }
          case 'activity': {
            const { data: queryActivity } = await getQueryActivity();
            rawSubItems = queryActivity;
            break;
          }
          case 'list': {
            const { data: filterSubItems } = await getFilterSubItems(
              option.value,
            );
            rawSubItems = filterSubItems;
            break;
          }
          case 'calculation_types': {
            const { data: filterSubItems } = await getFilterCalculationTypes();
            rawSubItems = filterSubItems;
            break;
          }
          case 'issue_status': {
            const { data: issueStatuses } =
              await getFilterIssueStatuses(projectId);
            rawSubItems = issueStatuses;
            break;
          }
          case 'issue_watchers': {
            const { data: issueWatchers } =
              await getFilterIssueWatcher(projectId);
            issueWatchers[0].valueName = getLocalString('labels', 'me');
            rawSubItems = issueWatchers;
            break;
          }
          case 'user_groups': {
            const { data: userGroups } = await getFilterIssueUserGroups();
            rawSubItems = userGroups;
            break;
          }
          case 'issue_priority': {
            const { data: issuePriorities } = await getFilterIssuePriorities();
            rawSubItems = issuePriorities;
            break;
          }
          case 'user_roles': {
            const { data: userRoles } = await getFilterIssueRoles();
            rawSubItems = userRoles;
            break;
          }
          case 'status': {
            const { data: projectStatuses } = await getFilterProjectStatuses();
            rawSubItems = projectStatuses;
            break;
          }
          case 'text':
          case 'file': {
            setIsDefaultFirstStepOpen(true);
            break;
          }
          case 'bool': {
            setIsDefaultFirstStepOpen(true);
            break;
          }
          case 'float': {
            setIsDefaultFirstStepOpen(true);
            rawSubItems = [];
            break;
          }
          case 'int': {
            break;
          }
          case 'date': {
            setIsDefaultFirstStepOpen(true);
            break;
          }
          default: {
            break;
          }
        }
        if (option.isUpSelectedItems) {
          const currentValue = Object.values(
            filterOptions.chosenFirstStepItems,
          ).filter(item => item.value === option.value);
          currentValue.forEach(item => {
            const currentSubLabels = item.subLabels.map(
              subLabel => subLabel && Number(subLabel.valueId),
            );
            currentSubLabels.forEach(rec => {
              const currentItem = rawSubItems.find(
                subItem => Number(subItem.valueId) === Number(rec),
              );
              const currentItemIndex = rawSubItems.indexOf(currentItem);
              rawSubItems.splice(0, 0, rawSubItems[currentItemIndex]);
              rawSubItems.splice(currentItemIndex + 1, 1);
            });
          });
        }
        handleFormatFirstStepItems();
        setValues(
          option.hasOwnProperty('isUpdateFirstStep')
            ? { ...filterOptions.chosenFirstStepItems }
            : {
                ...filterOptions.chosenFirstStepItems,
                [`${option.value}_`]: {
                  label: option.label,
                  value: option.value,
                  subLabels: [],
                  fieldType: option.fieldType,
                  type: option.type,
                  notParsedType: option.type,
                  locked: option.locked ? option.locked : false,
                },
              },
        );
        setFilterOptions(prevState => {
          return {
            ...prevState,
            availableSecondStepItems: rawSubItems,
            secondStepInputText: '',
            // SET TEMPORARY NAME FOR GET REQUEST [EXAMPLE: 'timesheet.project_']
            chosenFirstStepItems: option.hasOwnProperty('isUpdateFirstStep')
              ? { ...filterOptions.chosenFirstStepItems }
              : {
                  ...filterOptions.chosenFirstStepItems,
                  [`${option.value}_`]: {
                    label: option.label,
                    value: option.value,
                    subLabels: [],
                    fieldType: option.fieldType,
                    type: option.type,
                    notParsedType: option.type,
                    locked: option.locked ? option.locked : false,
                  },
                },
            // SAVE TEMPORARY FIRST STEP VALUES
            firstStepValues: {
              value: option.value,
              valueName: option.label,
              id: prevState.firstStepValues.id
                ? prevState.firstStepValues.id
                : option.id,
              fieldType: option.fieldType,
              type: option.type,
            },
          };
        });
      } catch (error) {
        handleFormatFirstStepItems();
      } finally {
        setIsSecondStepLoading(false);
      }
    },
    [
      getLocalString,
      handleGetNewQueryIssues,
      filterOptions.chosenFirstStepItems,
      state,
      fixedAnchorEl,
      setValues,
      handleFormatFirstStepItems,
    ],
  );
  const formatDateToMonthYear = dateString => {
    const date = dayjs(dateString);
    return `${date.format('MMMM YYYY')}`;
  };
  const formatDateToYear = dateString => {
    const date = dayjs(dateString);
    return `${date.format('YYYY')}`;
  };
  const getNewRowType = useCallback(value => {
    switch (value) {
      case '>t-':
        value = 'gt';
        return value;
      case '<t-':
        value = 'lt';
        return value;
      case '><t-':
        value = 'gt';
        return value;
      case 't-':
        value = 'eq';
        return value;
      default:
        return value;
    }
  }, []);

  const handleChooseSubItem = useCallback(
    ({ option, isMultiple = true, type, itemIndex }) => {
      const chosenItemsWithoutCurrent = Object.entries(
        filterOptions.chosenFirstStepItems,
      ).reduce((acc, rec) => {
        let valueForDelete = filterOptions.firstStepValues.value;
        if (option.hasOwnProperty('item') && option.hasOwnProperty('delete')) {
          valueForDelete = option.item;
        }
        if (rec[1].value === valueForDelete) {
          return acc;
        }
        return {
          ...acc,
          [rec[0]]: rec[1],
        };
      }, {});
      const subLabels =
        filterOptions.chosenFirstStepItems[filterOptions.firstStepValues.value]
          ?.subLabels || [];
      const isOptionIncludeLabel = subLabels
        .map(label => {
          if (!label) {
            return;
          }
          return label.valueId;
        })
        .includes(option.valueId);
      const formattedFirstStepItems = Object.values(
        filterOptions.chosenFirstStepItems,
      ).reduce((acc, rec) => {
        if (!rec.hasOwnProperty('value')) {
          return acc;
        }
        if (`${rec.value}`.split('').at(-1) === '_') {
          return acc;
        }
        return {
          ...acc,
          [rec.value]: rec,
        };
      }, {});
      if (option.hasOwnProperty('delete')) {
        setIsSecondStepPopperOpen(false);
        setFilterOptions(prevState => ({
          ...prevState,
          chosenFirstStepItems: chosenItemsWithoutCurrent,
        }));
        return setValues(
          defaultStep === 0
            ? chosenItemsWithoutCurrent
            : {
                ...chosenItemsWithoutCurrent,
                [`${filterOptions.firstStepValues.value}_`]: {
                  valueName: filterOptions.firstStepValues.valueName,
                  label: filterOptions.firstStepValues.valueName,
                  value: filterOptions.firstStepValues.value,
                  type: getNewRowType(filterOptions.firstStepValues.type),
                  fieldType: filterOptions.firstStepValues.fieldType,
                  subLabels: [],
                },
              },
        );
      }
      if (subLabels.length === 1 && isOptionIncludeLabel && itemIndex === 0) {
        if (defaultStep === 0) {
          return;
        }
      }
      const newChosenFirstStepItems = {
        ...formattedFirstStepItems,
        [filterOptions.firstStepValues.value]: {
          ...filterOptions.chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ],
          ...filterOptions.chosenFirstStepItems[
            filterOptions.firstStepValues.value
          ],
          valueName: filterOptions.firstStepValues.valueName,
          label: filterOptions.firstStepValues.valueName,
          value: filterOptions.firstStepValues.value,
          type: getNewRowType(filterOptions.firstStepValues.type),
          fieldType: filterOptions.firstStepValues.fieldType,
          subLabels:
            filterOptions.firstStepValues.type === 'null' ||
            filterOptions.firstStepValues.type === 'all'
              ? [null]
              : isOptionIncludeLabel ||
                  option.value === 'issue.issue_id' ||
                  option.value === 'issue.parent_issue'
                ? subLabels.filter(label => label.valueId !== option.valueId) ||
                  []
                : isMultiple
                  ? [...subLabels, option]
                  : [option],
        },
      };
      if (itemIndex === 1) {
        newChosenFirstStepItems[filterOptions.firstStepValues.value].subLabels =
          [subLabels[0], option];
      }
      if (itemIndex === 0 && filterOptions.firstStepValues.type === 'btw') {
        newChosenFirstStepItems[filterOptions.firstStepValues.value].subLabels =
          [option, subLabels[1]];
      }
      let newAvailableItems = filterOptions.availableSecondStepItems;
      if (
        isMultiple &&
        filterOptions.firstStepValues.type !== 'all' &&
        defaultStep !== 0
      ) {
        newAvailableItems = filterOptions.availableSecondStepItems.map(item => {
          if (item.valueId === option.valueId) {
            return { ...item, isChecked: !item.isChecked };
          }
          return item;
        });
        const sortAlphabetically = (a, b) => {
          return (
            a.valueName.localeCompare(b.valueName, 'en', {
              numeric: true,
              sensitivity: 'base',
            }) ||
            a.valueName.localeCompare(b.valueName, 'ru', {
              numeric: true,
              sensitivity: 'base',
            })
          );
        };

        const [checkedItems, uncheckedItems] = newAvailableItems.reduce(
          ([checked, unchecked], item) => {
            if (item.isChecked) {
              checked.push(item);
            } else {
              unchecked.push(item);
            }
            return [checked, unchecked];
          },
          [[], []],
        );
        checkedItems.sort(sortAlphabetically);
        uncheckedItems.sort(sortAlphabetically);

        newAvailableItems = [...checkedItems, ...uncheckedItems];
      }

      setFilterOptions(prevState => {
        return {
          ...prevState,
          chosenFirstStepItems: newChosenFirstStepItems,
          availableSecondStepItems: newAvailableItems,
        };
      });
      setValues(newChosenFirstStepItems);
      forceUpdate();
    },
    [getNewRowType, defaultStep, filterOptions, setValues],
  );

  const { current: props } = useRef({
    popper: params => {
      return <StyledAutoCompleteBox>{params.children}</StyledAutoCompleteBox>;
    },
    paper: params => (
      <StyledAutoCompleteBox>{params.children}</StyledAutoCompleteBox>
    ),
    optionLabel: option => option.label,
    renderOption: params => (
      <Box {...params} key={params['data-option-index']}>
        <Typography
          variant='h5'
          sx={{
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {params.key}
        </Typography>
      </Box>
    ),
    renderFilterMethod: (option, value) => {
      return option.value === value.value;
    },
  });

  const renderTypeOption = useCallback(
    type => {
      switch (type) {
        case 'boolean':
        case '1bool':
        case 'activity':
          return (
            <>
              <option value='eq'>{getLocalString('labels', 'eq')}</option>
              <option value='noteq'>{getLocalString('labels', 'noteq')}</option>
            </>
          );
        case 'issue_status':
        case 'status':
        case 'list':
        case 'user':
        case 'project':
        case 'parent':
        case 'tracker':
        case 'user_list':
        case 'contract_issues':
        case 'calculation_types':
          return (
            <>
              <option value='eq'>{getLocalString('labels', 'eq')}</option>
              <option value='noteq'>{getLocalString('labels', 'noteq')}</option>
              <option value='null'>{getLocalString('labels', 'null')}</option>
              <option value='all'>{getLocalString('labels', 'all')}</option>
            </>
          );
        case 'issues':
        case 'user_groups':
        case 'user_watchers':
        case 'issue_watchers':
        case 'issue_priority':
        case 'user_roles':
        case 'rp_issues':
          return (
            <>
              <option value='eq'>{getLocalString('labels', 'eq')}</option>
              <option value='noteq'>{getLocalString('labels', 'noteq')}</option>
              <option value='null'>{getLocalString('labels', 'null')}</option>
              <option value='all'>{getLocalString('labels', 'all')}</option>
            </>
          );
        case 'int':
        case 'float':
          return (
            <>
              <option value='eq'>{getLocalString('labels', 'eq')}</option>
              <option value='gte'>{'>='}</option>
              <option value='lte'>{'<='}</option>
              <option value='btw'>{getLocalString('labels', 'btw')}</option>
              <option value='null'>{getLocalString('labels', 'null')}</option>
              <option value='all'>{getLocalString('labels', 'all')}</option>
            </>
          );
        case 'spent_on':
        case 'date':
          return (
            <>
              <option value='eq'>{getLocalString('labels', 'eq')}</option>
              <option value='gte'>{'>='}</option>
              <option value='lte'>{'<='}</option>
              <option value='btw'>{getLocalString('labels', 'btw')}</option>
              <option value='Today'>{getLocalString('labels', 'today')}</option>
              <option value='Yesterday'>
                {getLocalString('labels', 'yesterday')}
              </option>
              <option value='ThisWeek'>
                {getLocalString('labels', 'thisweek')}
              </option>
              <option value='LastWeek'>
                {getLocalString('labels', 'lastweek')}
              </option>
              <option value='ThisMonth'>
                {getLocalString('labels', 'thismonth')}
              </option>
              <option value='LastMonth'>
                {getLocalString('labels', 'lastmonth')}
              </option>
              <option value='ThisYear'>
                {getLocalString('labels', 'thisyear')}
              </option>
              <option value='null'>{getLocalString('labels', 'null')}</option>
              <option value='all'>{getLocalString('labels', 'all')}</option>
            </>
          );
        case 'string':
        case 'text':
        case 'file':
          return (
            <>
              <option value='in'>{getLocalString('labels', 'in')}</option>
              <option value='notin'>{getLocalString('labels', 'notin')}</option>
              <option value='startswith'>
                {getLocalString('labels', 'startswith')}
              </option>
              <option value='endswith'>
                {getLocalString('labels', 'endswith')}
              </option>
              <option value='null'>{getLocalString('labels', 'null')}</option>
              <option value='all'>{getLocalString('labels', 'all')}</option>
            </>
          );
        case 'issue':
          return (
            <>
              <option value='eq'>{getLocalString('labels', 'eq')}</option>
              <option value='noteq'>{getLocalString('labels', 'noteq')}</option>
              <option value='null'>{getLocalString('labels', 'null')}</option>
              <option value='all'>{getLocalString('labels', 'all')}</option>
            </>
          );
        default:
          return <option>{type}</option>;
      }
    },
    [getLocalString],
  );

  const handleChangeFilterType = useCallback(
    e => {
      setIsDefaultFirstStepOpen(true);
      let newAvailableItems = filterOptions.availableSecondStepItems;
      if (defaultStep !== 0) {
        newAvailableItems = filterOptions.availableSecondStepItems.map(item => {
          if (item.isChecked) {
            return { ...item, isChecked: false };
          }
          return item;
        });
      }
      let chosenFirstStepItems;
      let defaultItemName = `${filterOptions.firstStepValues.value}_`;
      chosenFirstStepItems = {
        ...filterOptions.chosenFirstStepItems,
        [defaultItemName]: {
          ...filterOptions.chosenFirstStepItems[
            filterOptions.firstStepValues.value
          ],
          ...filterOptions.chosenFirstStepItems[defaultItemName],
          type: e.target.value,
          notParsedType: e.target.value,
          valueName: filterOptions.firstStepValues.valueName,
          label: filterOptions.firstStepValues.valueName,
          value: filterOptions.firstStepValues.value,
          fieldType: filterOptions.firstStepValues.fieldType,
          subLabels:
            filterOptions.chosenFirstStepItems[defaultItemName]?.subLabels
              ?.length > 0
              ? e.target.value.split('').includes('-')
                ? []
                : [
                    filterOptions.chosenFirstStepItems[defaultItemName]
                      .subLabels[0],
                  ]
              : [],
        },
      };
      if (
        filterOptions.chosenFirstStepItems[
          filterOptions.firstStepValues.value
        ]?.hasOwnProperty('type')
      ) {
        delete chosenFirstStepItems[filterOptions.firstStepValues.value];
        setIsClearDate(true);
        setTimeout(() => setIsClearDate(false), 100);
      }
      dayjs.extend(weekday);
      switch (e.target.value) {
        case 'Today':
          setIsDefaultFirstStepOpen(false);
          setIsSecondStepPopperOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'eq',
            subLabels: [
              {
                valueName: dayjs().format('DD.MM.YYYY'),
                valueId: dayjs().format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        // case 'nd':
        //   setIsSecondStepPopperOpen(false);
        //   setIsDefaultFirstStepOpen(false);
        //   chosenFirstStepItems[filterOptions.firstStepValues.value] = {
        //     ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
        //     type: 'eq',
        //     subLabels: [
        //       {
        //         valueName: dayjs().add(1, 'd').format('DD.MM.YYYY'),
        //         valueId: dayjs().add(1, 'd').format('YYYY-MM-DD'),
        //       },
        //     ],
        //   };
        //   delete chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`];
        //   break;
        case 'Yesterday':
          setIsSecondStepPopperOpen(false);
          setIsDefaultFirstStepOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'eq',
            subLabels: [
              {
                valueName: dayjs().subtract(1, 'd').format('DD.MM.YYYY'),
                valueId: dayjs().subtract(1, 'd').format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        case 'ThisWeek':
          setIsSecondStepPopperOpen(false);
          setIsDefaultFirstStepOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'btw',
            subLabels: [
              {
                valueName: dayjs().weekday(0).format('DD.MM.YYYY'),
                valueId: dayjs().weekday(0).format('YYYY-MM-DD'),
              },
              {
                valueName: dayjs().weekday(6).format('DD.MM.YYYY'),
                valueId: dayjs().weekday(6).format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        case 'LastWeek':
          setIsSecondStepPopperOpen(false);
          setIsDefaultFirstStepOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'btw',
            subLabels: [
              {
                valueName: dayjs()
                  .subtract(1, 'w')
                  .weekday(0)
                  .format('DD.MM.YYYY'),
                valueId: dayjs()
                  .subtract(1, 'w')
                  .weekday(0)
                  .format('YYYY-MM-DD'),
              },
              {
                valueName: dayjs()
                  .subtract(1, 'w')
                  .weekday(6)
                  .format('DD.MM.YYYY'),
                valueId: dayjs()
                  .subtract(1, 'w')
                  .weekday(6)
                  .format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        case 'LastTwoWeeks':
          setIsSecondStepPopperOpen(false);
          setIsDefaultFirstStepOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'btw',
            subLabels: [
              {
                valueName: dayjs()
                  .subtract(2, 'w')
                  .weekday(0)
                  .format('DD.MM.YYYY'),
                valueId: dayjs()
                  .subtract(2, 'w')
                  .weekday(0)
                  .format('YYYY-MM-DD'),
              },
              {
                valueName: dayjs()
                  .subtract(1, 'w')
                  .weekday(6)
                  .format('DD.MM.YYYY'),
                valueId: dayjs()
                  .subtract(1, 'w')
                  .weekday(6)
                  .format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        case 'ThisMonth':
          setIsSecondStepPopperOpen(false);
          setIsDefaultFirstStepOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'btw',
            subLabels: [
              {
                valueName: dayjs().startOf('month').format('DD.MM.YYYY'),
                valueId: dayjs().startOf('month').format('YYYY-MM-DD'),
              },
              {
                valueName: dayjs().endOf('month').format('DD.MM.YYYY'),
                valueId: dayjs().endOf('month').format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        case 'LastMonth':
          setIsDefaultFirstStepOpen(false);
          setIsSecondStepPopperOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'btw',
            subLabels: [
              {
                valueName: dayjs()
                  .subtract(1, 'month')
                  .startOf('month')
                  .format('DD.MM.YYYY'),
                valueId: dayjs()
                  .subtract(1, 'month')
                  .startOf('month')
                  .format('YYYY-MM-DD'),
              },
              {
                valueName: dayjs()
                  .subtract(1, 'month')
                  .endOf('month')
                  .format('DD.MM.YYYY'),
                valueId: dayjs()
                  .subtract(1, 'month')
                  .endOf('month')
                  .format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        case 'ThisYear':
          setIsDefaultFirstStepOpen(false);
          setIsSecondStepPopperOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'btw',
            subLabels: [
              {
                valueName: dayjs().startOf('year').format('DD.MM.YYYY'),
                valueId: dayjs().startOf('year').format('YYYY-MM-DD'),
              },
              {
                valueName: dayjs().endOf('year').format('DD.MM.YYYY'),
                valueId: dayjs().endOf('year').format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        case 'y':
          setIsDefaultFirstStepOpen(false);
          setIsSecondStepPopperOpen(false);
          chosenFirstStepItems[filterOptions.firstStepValues.value] = {
            ...chosenFirstStepItems[`${filterOptions.firstStepValues.value}_`],
            type: 'btw',
            subLabels: [
              {
                valueName: dayjs().startOf('year').format('DD.MM.YYYY'),
                valueId: dayjs().startOf('year').format('YYYY-MM-DD'),
              },
              {
                valueName: dayjs().endOf('year').format('DD.MM.YYYY'),
                valueId: dayjs().endOf('year').format('YYYY-MM-DD'),
              },
            ],
          };
          delete chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ];
          break;
        case 'btw':
          break;
        case '>t-':
          setIsSecondStepPopperOpen(true);
          chosenFirstStepItems[defaultItemName].type = 'gt';
          break;
        case '<t-':
          setIsSecondStepPopperOpen(true);
          chosenFirstStepItems[defaultItemName].type = 'lt';
          break;
        case '><t-':
          setIsSecondStepPopperOpen(true);
          chosenFirstStepItems[defaultItemName].type = 'gt';
          break;
        case 't-':
          setIsSecondStepPopperOpen(true);
          chosenFirstStepItems[defaultItemName].type = 'eq';
          break;
      }
      if (e.target.value === 'all' || e.target.value === 'null') {
        chosenFirstStepItems[filterOptions.firstStepValues.value] = {
          ...chosenFirstStepItems[defaultItemName],
          subLabels: [null],
        };
        delete chosenFirstStepItems[defaultItemName];
        setIsDefaultFirstStepOpen(false);
        setIsSecondStepPopperOpen(false);
      }
      setFilterOptions(prevState => {
        return {
          ...prevState,
          chosenFirstStepItems,
          firstStepValues: {
            ...prevState.firstStepValues,
            type: e.target.value,
          },
          availableSecondStepItems: newAvailableItems,
        };
      });
      setValues(chosenFirstStepItems);
    },

    [
      filterOptions.chosenFirstStepItems,
      filterOptions.firstStepValues,
      setValues,
    ],
  );

  const handleOpenFirstDefaultStepFilter = useCallback(
    e => {
      setIsDefaultFirstStepOpen(true);
      defaultStep === 1 && setAnchorEl(e.currentTarget);
      if (
        filterOptions.chosenFirstStepItems[filterOptions.firstStepValues.value]
      ) {
        return;
      }
      setFilterOptions(prevState => {
        return {
          ...prevState,
          chosenFirstStepItems: {
            ...prevState.chosenFirstStepItems,
            [`${prevState.firstStepValues.value}_`]: {
              ...prevState.chosenFirstStepItems[
                `${prevState.firstStepValues.value}_`
              ],
              label: prevState.firstStepValues.valueName,
              value: prevState.firstStepValues.value,
              type: prevState.firstStepValues.type,
              notParsedType: prevState.firstStepValues.type,
              subLabels: [],
            },
          },
        };
      });
      setValues({
        ...filterOptions.chosenFirstStepItems,
        [`${filterOptions.firstStepValues.value}_`]: {
          ...filterOptions.chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ],
          label: filterOptions.firstStepValues.valueName,
          value: filterOptions.firstStepValues.value,
          type: filterOptions.firstStepValues.type,
          notParsedType: filterOptions.firstStepValues.type,
          subLabels: [],
        },
      });
    },
    [
      defaultStep,
      filterOptions.chosenFirstStepItems,
      filterOptions.firstStepValues.type,
      filterOptions.firstStepValues.value,
      filterOptions.firstStepValues.valueName,
      setValues,
    ],
  );

  const renderSecondStepInput = useCallback(
    ({
      value,
      open,
      onOpen,
      renderInput,
      PaperComponent,
      PopperComponent,
      renderOption,
      className,
      placeholder,
    }) => {
      const autocompleteParams = {
        onChange: (e, newValue, _, { option }) =>
          handleChooseSubItem({ option }),
        noOptionsText: (
          <Typography variant='h5'>
            {getLocalString('errors', 'noResult')}
          </Typography>
        ),
      };
      const dateParams = {
        renderDay: (date, selectedDays, props) => {
          const onDaySelect = selectedDate => {
            handleChooseSubItem({
              option: {
                valueName: dayjs(selectedDate).format('DD.MM.YYYY'),
                valueId: dayjs(selectedDate).format('YYYY-MM-DD'),
              },
              isMultiple: true,
            });
          };
          return (
            <PickersDay
              {...props}
              key={date}
              outsideCurrentMonth={false}
              day={date}
              disableRipple
              onDaySelect={onDaySelect}
            />
          );
        },
        formatter: day =>
          `${day.charAt(0).toLowerCase()}${day.charAt(1).toLowerCase()}`,
      };
      let currentType =
        filterOptions.chosenFirstStepItems[filterOptions.firstStepValues.value]
          ?.type || '';
      if (
        filterOptions.chosenFirstStepItems[
          `${filterOptions.firstStepValues.value}_`
        ]?.hasOwnProperty('type')
      ) {
        currentType =
          filterOptions.chosenFirstStepItems[
            `${filterOptions.firstStepValues.value}_`
          ].type;
      }
      switch (value) {
        case 'spent_on':
        case 'date':
          return (
            <>
              <StyledFilterInputButton
                inputBoxStyle={true}
                activeStyled={
                  defaultStep !== 0 &&
                  (filterOptions.chosenFirstStepItems[
                    filterOptions.firstStepValues.value
                  ]?.notParsedType === 'null' ||
                    (filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels?.length > 0 &&
                      filterOptions.chosenFirstStepItems[
                        filterOptions.firstStepValues.value
                      ]?.subLabels[0]?.valueId.length > 0))
                }
                disableRipple
                variant='outlined'
                placeholder={placeholder}
                onClick={handleOpenFirstDefaultStepFilter}
              >
                {placeholder}
              </StyledFilterInputButton>
              <StyledAutoCompletePopper
                defaultFirstStepPaperStyle={true}
                dateFirstStepPaperStyle={defaultStep === 1}
                disablePortal
                open={open || currentType.split('').includes('-')}
                anchorEl={anchorEl}
                placement='bottom-start'
              >
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(228, 228, 239, 1)',
                    padding: 2,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      fontWeight: 700,
                      marginRight: 4,
                      marginLeft: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {filterOptions.firstStepValues.valueName}
                  </Typography>
                  <StyledSecondStepInput
                    native
                    value={
                      filterOptions.chosenFirstStepItems[
                        filterOptions.firstStepValues.value
                      ]?.notParsedType
                    }
                    onChange={handleChangeFilterType}
                    IconComponent={() => {
                      return (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            paddingRight: 2,
                            position: 'absolute',
                            right: 10,
                          }}
                        >
                          <ChevronIcon direction='down' viewBox='0 0 24 24' />
                        </Box>
                      );
                    }}
                    MenuProps={{
                      style: {
                        zIndex: 2222,
                      },
                    }}
                  >
                    {renderTypeOption(filterOptions.firstStepValues.fieldType)}
                  </StyledSecondStepInput>
                </Grid>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={language}
                >
                  <FilterDateBetween
                    filterOptions={filterOptions}
                    dateParams={dateParams}
                    handleChooseSubItem={handleChooseSubItem}
                    isClear={isClearDate}
                  />
                </LocalizationProvider>
              </StyledAutoCompletePopper>
            </>
          );
        case 'resPlan_date_to':
          return (
            <>
              <StyledFilterInputButton
                disableRipple
                variant='outlined'
                placeholder={placeholder}
                inputBoxStyle={true}
                activeStyled={
                  defaultStep !== 0 &&
                  (filterOptions.chosenFirstStepItems[
                    filterOptions.firstStepValues.value
                  ]?.notParsedType === 'null' ||
                    (filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels?.length > 0 &&
                      filterOptions.chosenFirstStepItems[
                        filterOptions.firstStepValues.value
                      ]?.subLabels[0]?.valueId.length > 0))
                }
                onClick={handleOpenFirstDefaultStepFilter}
              >
                {placeholder +
                  ' ' +
                  formatDateToMonthYear(
                    filterOptions?.chosenFirstStepItems['resourcePlan.date_to']
                      ?.subLabels[0]?.valueId,
                  )}
              </StyledFilterInputButton>
              <StyledAutoCompletePopper
                defaultFirstStepPaperStyle={true}
                dateFirstStepPaperStyle={defaultStep === 1}
                disablePortal
                open={open || currentType.split('').includes('-')}
                anchorEl={anchorEl}
                placement='bottom-start'
              >
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(228, 228, 239, 1)',
                    padding: 2,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      fontWeight: 700,
                      marginRight: 4,
                      marginLeft: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {filterOptions.firstStepValues.valueName}
                  </Typography>
                </Grid>
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 4,
                    width: 'auto !important',
                  }}
                >
                  <IconButton
                    width={24}
                    height={24}
                    onClick={() => {
                      const currentDate =
                        filterOptions?.chosenFirstStepItems[
                          'resourcePlan.date_to'
                        ]?.subLabels[0]?.valueId;

                      if (currentDate) {
                        const newDate = dayjs(currentDate).subtract(1, 'year');

                        const dateOption = {
                          month: newDate.month() + 1,
                          year: newDate.year(),
                          valueName: newDate.format('DD.MM.YYYY'),
                          valueId: newDate.format('YYYY-MM-DD'),
                        };

                        handleChooseSubItem({
                          option: dateOption,
                          isMultiple: false,
                        });
                      }
                    }}
                  >
                    <ChevronIcon
                      width={24}
                      height={24}
                      direction='left'
                      viewBox='0 0 24 24'
                    />
                  </IconButton>
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDateToYear(
                      filterOptions?.chosenFirstStepItems[
                        'resourcePlan.date_to'
                      ]?.subLabels[0]?.valueId,
                    )}
                  </Typography>
                  <IconButton
                    width={24}
                    height={24}
                    onClick={() => {
                      const currentDate =
                        filterOptions?.chosenFirstStepItems[
                          'resourcePlan.date_to'
                        ]?.subLabels[0]?.valueId;

                      if (currentDate) {
                        const newDate = dayjs(currentDate).add(1, 'year');

                        const dateOption = {
                          month: newDate.month() + 1,
                          year: newDate.year(),
                          valueName: newDate.format('DD.MM.YYYY'),
                          valueId: newDate.format('YYYY-MM-DD'),
                        };

                        handleChooseSubItem({
                          option: dateOption,
                          isMultiple: false,
                        });
                      }
                    }}
                  >
                    <ChevronIcon
                      width={24}
                      height={24}
                      direction='right'
                      viewBox='0 0 24 24'
                    />
                  </IconButton>
                </Grid>
                <StyledCalendarResplanRoot dateAdapter={AdapterDayjs}>
                  <FilterDateBetween
                    showMonthPicker={true} // Добавьте эту строку, чтобы показать новый блок
                    filterOptions={filterOptions}
                    dateParams={dateParams}
                    handleChooseSubItem={handleChooseSubItem}
                    isClear={isClearDate}
                  />
                </StyledCalendarResplanRoot>
              </StyledAutoCompletePopper>
            </>
          );
        case 'resPlan_date_from':
          return (
            <>
              <StyledFilterInputButton
                disableRipple
                variant='outlined'
                placeholder={placeholder}
                inputBoxStyle={true}
                activeStyled={
                  defaultStep !== 0 &&
                  (filterOptions.chosenFirstStepItems[
                    filterOptions.firstStepValues.value
                  ]?.notParsedType === 'null' ||
                    (filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels?.length > 0 &&
                      filterOptions.chosenFirstStepItems[
                        filterOptions.firstStepValues.value
                      ]?.subLabels[0]?.valueId.length > 0))
                }
                onClick={handleOpenFirstDefaultStepFilter}
              >
                {placeholder +
                  ' ' +
                  formatDateToMonthYear(
                    filterOptions?.chosenFirstStepItems[
                      'resourcePlan.date_from'
                    ]?.subLabels[0]?.valueId,
                  )}{' '}
              </StyledFilterInputButton>
              <StyledAutoCompletePopper
                defaultFirstStepPaperStyle={true}
                dateFirstStepPaperStyle={defaultStep === 1}
                disablePortal
                open={open || currentType.split('').includes('-')}
                anchorEl={anchorEl}
                placement='bottom-start'
              >
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(228, 228, 239, 1)',
                    padding: 2,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      fontWeight: 700,
                      marginRight: 4,
                      marginLeft: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {filterOptions.firstStepValues.valueName}
                  </Typography>
                </Grid>
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 4,
                    width: 'auto !important',
                  }}
                >
                  <IconButton
                    width={24}
                    height={24}
                    onClick={() => {
                      const currentDate =
                        filterOptions?.chosenFirstStepItems[
                          'resourcePlan.date_from'
                        ]?.subLabels[0]?.valueId;

                      if (currentDate) {
                        const newDate = dayjs(currentDate).subtract(1, 'year');

                        const dateOption = {
                          month: newDate.month() + 1,
                          year: newDate.year(),
                          valueName: newDate.format('DD.MM.YYYY'),
                          valueId: newDate.format('YYYY-MM-DD'),
                        };

                        handleChooseSubItem({
                          option: dateOption,
                          isMultiple: false,
                        });
                      }
                    }}
                  >
                    <ChevronIcon
                      width={24}
                      height={24}
                      direction='left'
                      viewBox='0 0 24 24'
                    />
                  </IconButton>
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDateToYear(
                      filterOptions?.chosenFirstStepItems[
                        'resourcePlan.date_from'
                      ]?.subLabels[0]?.valueId,
                    )}
                  </Typography>
                  <IconButton
                    width={24}
                    height={24}
                    onClick={() => {
                      const currentDate =
                        filterOptions?.chosenFirstStepItems[
                          'resourcePlan.date_from'
                        ]?.subLabels[0]?.valueId;

                      if (currentDate) {
                        const newDate = dayjs(currentDate).add(1, 'year');

                        const dateOption = {
                          month: newDate.month() + 1,
                          year: newDate.year(),
                          valueName: newDate.format('DD.MM.YYYY'),
                          valueId: newDate.format('YYYY-MM-DD'),
                        };

                        handleChooseSubItem({
                          option: dateOption,
                          isMultiple: false,
                        });
                      }
                    }}
                  >
                    <ChevronIcon
                      width={24}
                      height={24}
                      direction='right'
                      viewBox='0 0 24 24'
                    />
                  </IconButton>
                </Grid>
                <StyledCalendarResplanRoot dateAdapter={AdapterDayjs}>
                  <FilterDateBetween
                    showMonthPicker={true} // Добавьте эту строку, чтобы показать новый блок
                    filterOptions={filterOptions}
                    dateParams={dateParams}
                    handleChooseSubItem={handleChooseSubItem}
                    isClear={isClearDate}
                  />
                </StyledCalendarResplanRoot>
              </StyledAutoCompletePopper>
            </>
          );
        case 'string':
        case 'text':
        case 'file':
          return (
            <>
              <StyledFilterInputButton
                disableRipple
                variant='outlined'
                placeholder={placeholder}
                inputBoxStyle={true}
                activeStyled={
                  defaultStep !== 0 &&
                  (filterOptions.chosenFirstStepItems[
                    filterOptions.firstStepValues.value
                  ]?.notParsedType === 'null' ||
                    (filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels?.length > 0 &&
                      filterOptions.chosenFirstStepItems[
                        filterOptions.firstStepValues.value
                      ]?.subLabels[0]?.valueId.length > 0))
                }
                onClick={handleOpenFirstDefaultStepFilter}
              >
                {placeholder}
              </StyledFilterInputButton>
              <StyledAutoCompletePopper
                disablePortal
                open={isDefaultFirstStepOpen}
                anchorEl={anchorEl}
                placement='bottom-start'
              >
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(228, 228, 239, 1)',
                    padding: 2,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      fontWeight: 700,
                      marginRight: 4,
                      marginLeft: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {filterOptions.firstStepValues.valueName}
                  </Typography>
                  <StyledSecondStepInput
                    native
                    value={filterOptions.firstStepValues.type}
                    onChange={handleChangeFilterType}
                    IconComponent={() => {
                      return (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            paddingRight: 2,
                            position: 'absolute',
                            right: 10,
                          }}
                        >
                          <ChevronIcon direction='down' viewBox='0 0 24 24' />
                        </Box>
                      );
                    }}
                    MenuProps={{
                      style: {
                        zIndex: 2222,
                      },
                    }}
                  >
                    {renderTypeOption(filterOptions.firstStepValues.fieldType)}
                  </StyledSecondStepInput>
                </Grid>
                <StyledFilterInputTextField
                  secondInputStyle={true}
                  onChange={e =>
                    handleChooseSubItem({
                      option: {
                        valueName: e.target.value,
                        valueId: e.target.value,
                      },
                      isMultiple: false,
                    })
                  }
                  variant='outlined'
                  value={
                    filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels[0]?.valueId || ''
                  }
                  placeholder={placeholder}
                />
              </StyledAutoCompletePopper>
            </>
          );
        case 'bool':
        case 'boolean':
          return (
            <>
              <StyledFilterInputButton
                disableRipple
                variant='outlined'
                placeholder={placeholder}
                inputBoxStyle={true}
                activeStyled={
                  defaultStep !== 0 &&
                  (filterOptions.chosenFirstStepItems[
                    filterOptions.firstStepValues.value
                  ]?.notParsedType === 'null' ||
                    (filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels?.length > 0 &&
                      filterOptions.chosenFirstStepItems[
                        filterOptions.firstStepValues.value
                      ]?.subLabels[0]?.valueId.length > 0))
                }
                onClick={handleOpenFirstDefaultStepFilter}
              >
                {placeholder}
              </StyledFilterInputButton>
              <StyledAutoCompletePopper
                disablePortal
                open={isDefaultFirstStepOpen}
                anchorEl={anchorEl}
                placement='bottom-start'
              >
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid rgba(228, 228, 239, 1)',
                    padding: 2,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      fontWeight: 700,
                      marginRight: 4,
                      marginLeft: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {filterOptions.firstStepValues.valueName}
                  </Typography>
                  <StyledBoolClearButton
                    variant='defaultGreyPrimary'
                    onClick={() =>
                      handleChooseSubItem({ option: { delete: true } })
                    }
                  >
                    <Typography variant='h5'>
                      {getLocalString('buttons', 'clear')}
                    </Typography>
                  </StyledBoolClearButton>
                </Grid>
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <RadioGroup
                    onChange={e => {
                      handleChooseSubItem({
                        option: {
                          valueName: e.target.value,
                          valueId: e.target.value,
                        },
                        isMultiple: false,
                      });
                    }}
                  >
                    <StyledBooleanLabel
                      value='true'
                      checked={
                        filterOptions.chosenFirstStepItems[
                          filterOptions.firstStepValues.value
                        ]?.subLabels[0]?.valueId === 'true'
                      }
                      control={
                        <Radio
                          disableRipple
                          icon={
                            <CheckboxIcon
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                          }
                          checkedIcon={
                            <CheckboxIconChecked
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                          }
                        />
                      }
                      label={
                        <Typography variant='h5'>
                          {getLocalString('labels', 'yes')}
                        </Typography>
                      }
                    />
                    <StyledBooleanLabel
                      value='false'
                      checked={
                        filterOptions.chosenFirstStepItems[
                          filterOptions.firstStepValues.value
                        ]?.subLabels[0]?.valueId === 'false'
                      }
                      control={
                        <Radio
                          disableRipple
                          icon={
                            <CheckboxIcon
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                          }
                          checkedIcon={
                            <CheckboxIconChecked
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                          }
                        />
                      }
                      label={
                        <Typography variant='h5'>
                          {getLocalString('labels', 'no')}
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </Grid>
              </StyledAutoCompletePopper>
            </>
          );
        case 'int':
        case 'float':
          return (
            <>
              <StyledFilterInputButton
                disableRipple
                variant='outlined'
                placeholder={placeholder}
                inputBoxStyle={true}
                activeStyled={
                  defaultStep !== 0 &&
                  (filterOptions.chosenFirstStepItems[
                    filterOptions.firstStepValues.value
                  ]?.notParsedType === 'null' ||
                    (filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels?.length > 0 &&
                      filterOptions.chosenFirstStepItems[
                        filterOptions.firstStepValues.value
                      ]?.subLabels[0]?.valueId.length > 0))
                }
                onClick={handleOpenFirstDefaultStepFilter}
              >
                {placeholder}
              </StyledFilterInputButton>
              <StyledAutoCompletePopper
                disablePortal
                open={isDefaultFirstStepOpen}
                anchorEl={anchorEl}
                placement='bottom-start'
              >
                <Grid
                  container
                  wrap='nowrap'
                  sx={{
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(228, 228, 239, 1)',
                    padding: 2,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      fontWeight: 700,
                      marginRight: 4,
                      marginLeft: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {filterOptions.firstStepValues.valueName}
                  </Typography>
                  <StyledSecondStepInput
                    native
                    value={filterOptions.firstStepValues.type}
                    onChange={handleChangeFilterType}
                    IconComponent={() => {
                      return (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            paddingRight: 2,
                            position: 'absolute',
                            right: 10,
                          }}
                        >
                          <ChevronIcon direction='down' viewBox='0 0 24 24' />
                        </Box>
                      );
                    }}
                    MenuProps={{
                      style: {
                        zIndex: 2222,
                      },
                    }}
                  >
                    {renderTypeOption(filterOptions.firstStepValues.fieldType)}
                  </StyledSecondStepInput>
                </Grid>
                <StyledFilterInputTextField
                  secondInputStyle={true}
                  value={
                    filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels[0]?.valueId || ''
                  }
                  onChange={e => {
                    handleChooseSubItem({
                      option: {
                        valueName: e.target.value,
                        valueId: e.target.value,
                      },
                      isMultiple: false,
                      type: 1,
                      itemIndex: 0,
                    });
                  }}
                  variant='outlined'
                  placeholder={placeholder}
                  type='number'
                  step={0.1}
                />
                {currentType === 'btw' && (
                  <StyledFilterInputTextField
                    secondInputStyle={true}
                    value={
                      filterOptions.chosenFirstStepItems[
                        filterOptions.firstStepValues.value
                      ]?.subLabels[1]?.valueId || ''
                    }
                    onChange={e => {
                      handleChooseSubItem({
                        option: {
                          valueName: e.target.value,
                          valueId: e.target.value,
                        },
                        isMultiple: false,
                        type: 1,
                        itemIndex: 1,
                      });
                    }}
                    variant='outlined'
                    placeholder={placeholder}
                    type='number'
                    step={0.1}
                  />
                )}
              </StyledAutoCompletePopper>
            </>
          );
        case 'group_by':
          if (defaultStep === 1) {
            return (
              <StyledGroupByFilter
                name={filterOptions.firstStepValues.valueName}
                value={
                  filterOptions.chosenFirstStepItems[
                    filterOptions.firstStepValues.value
                  ]?.subLabels[0]?.valueId || ''
                }
                exclusive
                aria-label={filterOptions.firstStepValues.valueName}
                onChange={e => {
                  const newValue = e.target.value;
                  const currentValue =
                    filterOptions.chosenFirstStepItems[
                      filterOptions.firstStepValues.value
                    ]?.subLabels[0]?.valueId || '';

                  if (newValue === currentValue) {
                    return;
                  }

                  handleChooseSubItem({
                    option: { valueName: newValue, valueId: newValue },
                    isMultiple: false,
                  });
                }}
              >
                {filterOptions.availableSecondStepItems.map((filter, index) => {
                  const statusLabelTranslated = filter.valueName;
                  return (
                    <StyledResPlanToggle key={index} value={filter.valueId}>
                      {statusLabelTranslated}
                    </StyledResPlanToggle>
                  );
                })}
              </StyledGroupByFilter>
            );
          }
          break;
        case 'status':
          if (defaultStep === 1) {
            return (
              <StyledStatusFilterItem
                name={filterOptions.firstStepValues.valueName}
                value={
                  filterOptions.chosenFirstStepItems[
                    filterOptions.firstStepValues.value
                  ]?.subLabels[0]?.valueId || ''
                }
                exclusive
                aria-label={filterOptions.firstStepValues.valueName}
                onChange={e => {
                  handleChooseSubItem({
                    option: {
                      valueName: e.target.value,
                      valueId: e.target.value,
                    },
                    isMultiple: false,
                  });
                }}
              >
                {filterOptions.availableSecondStepItems.map((filter, index) => {
                  const statusLabelTranslated = filter.valueName;
                  return (
                    <ToggleButton key={index} value={filter.valueId}>
                      {statusLabelTranslated}
                    </ToggleButton>
                  );
                })}
              </StyledStatusFilterItem>
            );
          }
          break;
        default:
          return (
            <Autocomplete
              forcePopupIcon={false}
              multiple
              disablePortal
              disableCloseOnSelect
              open={open}
              onOpen={onOpen}
              clearIcon={null}
              renderInput={renderInput}
              renderOption={renderOption}
              onChange={autocompleteParams.onChange}
              getOptionLabel={option => option.valueName}
              noOptionsText={autocompleteParams.noOptionsText}
              options={filterOptions.availableSecondStepItems}
              value={Object.values(filterOptions.chosenFirstStepItems)}
              isOptionEqualToValue={(option, value) => {
                if (value.value === filterOptions.firstStepValues.value) {
                  return Boolean(
                    value.subLabels.find(label => {
                      if (!label) {
                        return;
                      }
                      return `${label.valueId}` === `${option.valueId}`;
                    }),
                  );
                }
                return false;
              }}
              slots={{
                paper: PaperComponent,
                popper: PopperComponent,
              }}
              slotProps={{
                chip: {
                  sx: {
                    display: 'none',
                  },
                  //component: StyledSubItemChip,
                  deleteIcon: null,
                },
              }}
            />
          );
      }
    },
    [
      language,
      getLocalString,
      filterOptions,
      defaultStep,
      handleChooseSubItem,
      handleOpenFirstDefaultStepFilter,
      isDefaultFirstStepOpen,
      anchorEl,
      handleChangeFilterType,
      renderTypeOption,
      isClearDate,
    ],
  );

  const debouncedValueLogging = useDebouncedFunction(
    newValue => handleGetNewQueryIssues({ value: newValue }),
    500,
  );

  const handleChangeSecondStepInput = e => {
    debouncedValueLogging(e.target.value);
    setFilterOptions(prevState => ({
      ...prevState,
      secondStepInputText: e.target.value,
      availableSecondStepItems:
        e.target.value === '' ? [] : prevState.availableSecondStepItems,
    }));
  };

  let arrowIconShow =
    filterOptions.firstStepValues.fieldType === 'list' ||
    filterOptions.firstStepValues.fieldType === 'user_list' ||
    filterOptions.firstStepValues.fieldType === 'issues' ||
    filterOptions.firstStepValues.fieldType === 'contract_issues' ||
    filterOptions.firstStepValues.fieldType === 'project' ||
    filterOptions.firstStepValues.fieldType === 'parent' ||
    filterOptions.firstStepValues.fieldType === 'user' ||
    filterOptions.firstStepValues.fieldType === 'issue_status' || //for agileFilters
    filterOptions.firstStepValues.fieldType === 'calculation_types' ||
    filterOptions.firstStepValues.fieldType === 'issue' ||
    filterOptions.firstStepValues.fieldType === 'tracker' ||
    filterOptions.firstStepValues.fieldType === 'issue_priority' ||
    filterOptions.firstStepValues.fieldType === 'issue_watchers';
  return (
    <>
      {defaultStep === 1 && (
        <>
          <ClickAwayListener onClickAway={handleCloseAll}>
            <Box
              sx={{
                width: '100%',
                position: 'relative',
              }}
            >
              {renderSecondStepInput({
                value: filterOptions.firstStepValues.fieldType,
                open: isDefaultFirstStepOpen,
                onOpen: handleOpenFirstDefaultStepFilter,
                renderInput: params => {
                  return (
                    <Box
                      key={params.value}
                      onFocus={params.inputProps.onFocus}
                      sx={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        position: 'relative',
                      }}
                    >
                      <StyledFilterInputTextField
                        defaultFirstStepStyle={true}
                        defaultActiveStyle={
                          filterOptions.chosenFirstStepItems[
                            filterOptions.firstStepValues.value
                          ]?.notParsedType === 'null' ||
                          (filterOptions.chosenFirstStepItems[
                            filterOptions.firstStepValues.value
                          ]?.subLabels !== 'undefined' &&
                            filterOptions.chosenFirstStepItems[
                              filterOptions.firstStepValues.value
                            ]?.subLabels?.length > 0 &&
                            filterOptions.chosenFirstStepItems[
                              filterOptions.firstStepValues.value
                            ]?.subLabels[0]?.valueId?.length > 0)
                        }
                        variant='outlined'
                        {...params}
                        placeholder={filterOptions.firstStepValues.valueName}
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            style: {
                              width: '100%',
                            },
                            onChange: e => {
                              if (
                                filterOptions.firstStepValues.fieldType ===
                                'issue'
                              ) {
                                handleChangeSecondStepInput(e);
                              }
                              return params.inputProps.onChange(e);
                            },
                            [filterOptions.firstStepValues.fieldType ===
                              'issue' && 'value']:
                              filterOptions.secondStepInputText,
                          },
                        }}
                      />
                      {arrowIconShow && (
                        <StyledTypeArrow
                          sx={{
                            width: 24,
                            height: 24,
                            position: 'absolute',
                            right: 10,
                            top: 0,
                            bottom: 0,
                            margin: 'auto',
                            zIndex: 20,
                          }}
                        >
                          <ChevronIcon viewBox='0 0 24 24' direction='down' />
                        </StyledTypeArrow>
                      )}
                    </Box>
                  );
                },
                PaperComponent: params => (
                  <StyledAutoCompletePaper
                    defaultFirstStepPaperStyle={true}
                    sx={{
                      visibility: !isDefaultFirstStepOpen ? 'hidden' : 'unset',
                    }}
                  >
                    <Grid
                      container
                      wrap='nowrap'
                      sx={{
                        alignItems: 'center',
                        borderBottom: '1px solid rgba(228, 228, 239, 1)',
                        padding: 2,
                      }}
                    >
                      <Typography
                        variant='h5'
                        sx={{
                          fontWeight: 700,
                          marginRight: 4,
                          marginLeft: 2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {filterOptions.firstStepValues.valueName}
                      </Typography>
                      <StyledSecondStepInput
                        native
                        value={filterOptions.firstStepValues.type}
                        onChange={handleChangeFilterType}
                        IconComponent={() => {
                          return (
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                paddingRight: 2,
                                position: 'absolute',
                                right: 10,
                              }}
                            >
                              <ChevronIcon
                                direction='down'
                                viewBox='0 0 24 24'
                              />
                            </Box>
                          );
                        }}
                        MenuProps={{
                          style: {
                            zIndex: 2222,
                          },
                        }}
                      >
                        {renderTypeOption(
                          filterOptions.firstStepValues.fieldType,
                        )}
                      </StyledSecondStepInput>
                    </Grid>
                    {params.children}
                  </StyledAutoCompletePaper>
                ),
                PopperComponent: props.popper,
                renderOption: props.renderOption,
                //className: classes.filterDefaultFirstStepInput,
                placeholder: filterOptions.firstStepValues.valueName,
              })}
            </Box>
          </ClickAwayListener>
        </>
      )}
      {defaultStep === 0 && (
        <>
          <ClickAwayListener onClickAway={handleCloseAll}>
            <Box>
              <StyledFilterAutoComplete
                multiple
                disablePortal
                disableClearable
                open={isFirstStepPopperOpen}
                onChange={handleChangeFilter}
                renderOption={(params, option, { selected }) => (
                  <Box
                    {...params}
                    key={params['data-option-index']}
                    disabled={true}
                  >
                    <Typography
                      disabled={option.locked}
                      variant='h5'
                      sx={{
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {params.key}
                    </Typography>
                  </Box>
                )}
                getOptionDisabled={option => !!option.locked}
                getOptionLabel={props.optionLabel}
                onOpen={handleOpenFilterFirstStep}
                options={filterOptions.availableFirstStepItems}
                isOptionEqualToValue={props.renderFilterMethod}
                value={Object.values(filterOptions.chosenFirstStepItems)}
                renderTags={(values, _, state) => {
                  return values.map(value => {
                    return (
                      <Chip
                        style={{ cursor: 'pointer' }}
                        data-item-id={value.value}
                        disabled={value.locked}
                        key={value.value}
                        onDelete={e => {
                          let newType =
                            e.currentTarget.attributes['data-item-id'].value;
                          if (
                            !filterOptions.chosenFirstStepItems[
                              newType
                            ]?.hasOwnProperty('value')
                          ) {
                            newType = `${newType}_`;
                          }
                          e.preventDefault();
                          e.stopPropagation();
                          return handleChooseSubItem({
                            option: {
                              delete: true,
                              item: filterOptions.chosenFirstStepItems[newType]
                                .value,
                            },
                          });
                        }}
                        onMouseDown={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          let newType =
                            e.currentTarget.attributes['data-item-id'].value;
                          if (
                            newType === filterOptions.firstStepValues.value &&
                            isSecondStepPopperOpen
                          ) {
                            return;
                          }
                          if (
                            !filterOptions.chosenFirstStepItems[
                              newType
                            ]?.hasOwnProperty('value')
                          ) {
                            newType = `${newType}_`;
                          }
                          handleChangeFilter(e, _, _, {
                            option: {
                              label:
                                filterOptions.chosenFirstStepItems[newType]
                                  .label,
                              value:
                                filterOptions.chosenFirstStepItems[newType]
                                  .value,
                              valueName:
                                filterOptions.chosenFirstStepItems[newType]
                                  .valueName,
                              fieldType:
                                filterOptions.chosenFirstStepItems[newType]
                                  .fieldType,
                              type: filterOptions.chosenFirstStepItems[newType]
                                .type,
                              notParsedType:
                                filterOptions.chosenFirstStepItems[newType]
                                  .type,
                              locked: filterOptions.chosenFirstStepItems[
                                newType
                              ].locked
                                ? filterOptions.chosenFirstStepItems[newType]
                                    .locked
                                : false,
                              isUpdateFirstStep: true,
                              isUpSelectedItems: true,
                            },
                          });
                        }}
                        label={value.label}
                        {...{
                          ...state.ChipProps,
                          deleteIcon: {
                            ...state.ChipProps.deleteIcon,
                            props: {
                              ...state.ChipProps.deleteIcon.props,
                              'data-item-id': value.value,
                            },
                          },
                        }}
                      />
                    );
                  });
                }}
                renderInput={params => {
                  return (
                    <Box onMouseDown={params.inputProps.onMouseDown}>
                      <StyledFilterInputTextField
                        variant='outlined'
                        {...params}
                        placeholder={
                          Object.values(filterOptions.chosenFirstStepItems)
                            .length > 0
                            ? ''
                            : getLocalString('labels', 'select')
                        }
                        slotProps={{
                          htmlInput: { ...params.inputProps, disabled: true },
                        }}
                      />
                    </Box>
                  );
                }}
                noOptionsText={
                  <Typography variant='h5'>
                    {getLocalString('errors', 'noResult')}
                  </Typography>
                }
                slots={{
                  paper: params => {
                    return (
                      <StyledAutoCompletePopper
                        disablePortal
                        open={isFirstStepPopperOpen}
                        anchorEl={anchorEl}
                        placement='bottom-start'
                      >
                        <StyledAutoCompletePaper>
                          {params.children}
                        </StyledAutoCompletePaper>
                      </StyledAutoCompletePopper>
                    );
                  },

                  popper: props.popper,
                }}
                slotProps={{
                  chip: {
                    component: StyledFilterChip,
                    deleteIcon: (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          position: 'relative',
                          zIndex: 2000,
                        }}
                      >
                        <ClearIcon viewBox='0 0 24 24' color='primary' />
                      </Box>
                    ),
                  },
                }}
              />
              {!filtersOnlySelect ? (
                <StyledAutoCompletePopper
                  disablePortal
                  open={isSecondStepPopperOpen}
                  anchorEl={anchorEl}
                  placement='bottom-start'
                >
                  {isSecondStepLoading ? (
                    <Grid
                      container
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        minHeight: '200px',
                      }}
                    >
                      <CircularProgress color='secondary' />
                    </Grid>
                  ) : (
                    <Box>
                      <Grid container direction='column' wrap='nowrap'>
                        <Grid
                          container
                          wrap='nowrap'
                          sx={{
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(228, 228, 239, 1)',
                            padding: 2,
                          }}
                        >
                          <Typography
                            variant='h5'
                            sx={{
                              fontWeight: 700,
                              marginRight: 4,
                              marginLeft: 2,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {filterOptions.firstStepValues.valueName}
                          </Typography>
                          <StyledSecondStepInput
                            native
                            value={
                              filterOptions.chosenFirstStepItems[
                                filterOptions.firstStepValues.value
                              ]?.type ||
                              filterOptions.chosenFirstStepItems[
                                `${filterOptions.firstStepValues.value}_`
                              ]?.type
                            }
                            onChange={handleChangeFilterType}
                            IconComponent={() => {
                              return (
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    paddingRight: 2,
                                    position: 'absolute',
                                    right: 10,
                                  }}
                                >
                                  <ChevronIcon
                                    direction='down'
                                    viewBox='0 0 24 24'
                                  />
                                </Box>
                              );
                            }}
                            MenuProps={{
                              style: {
                                zIndex: 2222,
                              },
                            }}
                          >
                            {renderTypeOption(
                              filterOptions.firstStepValues.fieldType,
                            )}
                          </StyledSecondStepInput>
                        </Grid>
                      </Grid>
                      {renderSecondStepInput({
                        value: filterOptions.firstStepValues.fieldType,
                        open: isSecondStepPopperOpen,
                        onOpen: null,
                        renderInput: params => {
                          return (
                            <Box
                              onMouseDown={params.inputProps.onMouseDown}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <StyledFilterInputTextField
                                {...params}
                                fullWidth
                                variant='outlined'
                                placeholder={getLocalString('labels', 'input')}
                                slotProps={{
                                  htmlInput: {
                                    ...params.inputProps,
                                    style: {
                                      width: '100%',
                                    },
                                    onChange: e => {
                                      if (
                                        filterOptions.firstStepValues
                                          .fieldType === 'issue'
                                      ) {
                                        handleChangeSecondStepInput(e);
                                      }
                                      return params.inputProps.onChange(e);
                                    },
                                    [filterOptions.firstStepValues.fieldType ===
                                      'issue' && 'value']:
                                      filterOptions.secondStepInputText,
                                  },
                                }}
                              />
                              <Divider orientation='horizontal' flexItem />
                            </Box>
                          );
                        },
                        PaperComponent: props.paper,
                        PopperComponent: props.popper,
                        renderOption: props.renderOption,
                        //className: classes.filterSecondInput,
                        placeholder: getLocalString('labels', 'input'),
                      })}
                    </Box>
                  )}
                </StyledAutoCompletePopper>
              ) : null}
            </Box>
          </ClickAwayListener>
        </>
      )}
    </>
  );
};

export default memo(CustomFilter);
